export default defineNuxtRouteMiddleware(async (to) => {
  if (to.path.startsWith('/api/') || to.path === '/auth/callback') return

  const store = useAuthStore()

  /**
   * Suspend la navigation le temps que le navigateur quitte la page.
   *
   * `window.location.href` ne part pas sur-le-champ : le navigateur termine
   * d'abord le travail en cours. Rendre la main pendant ce délai laisse le
   * routeur sans route, et Nuxt affiche sa page d'erreur par défaut — un
   * « 404 Page introuvable » en plein écran juste avant le départ.
   *
   * Une promesse jamais résolue tient la navigation ouverte jusqu'au
   * changement de page. Elle ne fuit pas : le document est détruit avec elle.
   */
  const suspendreJusquAuDepart = () =>
    new Promise<void>(() => {}) as unknown as ReturnType<typeof abortNavigation>

  // Toujours tenter fetchSession si pas encore authentifié
  if (!store.authenticated) {
    await store.fetchSession()
  }

  // La déconnexion a déjà lancé le départ vers le SSO : on attend, sans quoi
  // le même 404 s'afficherait le temps de la bascule.
  if (store.loggingOut) return suspendreJusquAuDepart()

  if (!store.authenticated) {
    // Une session illisible pour cause de panne — SSO injoignable, 500 — n'est
    // pas une session absente. Renvoyer vers le SSO dans ce cas n'y remédie
    // pas : le SSO est justement ce qui ne répond pas, et l'utilisateur
    // rebondit entre les deux applications. On affiche l'erreur, qui dit ce
    // qu'il en est.
    if (store.unreachable) {
      throw createError({
        statusCode: 503,
        message: 'Service d\'authentification momentanément indisponible. Réessayez dans quelques instants.',
      })
    }

    // `fullPath` garde la query : un lien partagé (liste filtrée) survit au
    // passage par le SSO.
    let state = to.fullPath
    if (state.startsWith('/api/') || to.path.includes('callback')) {
      state = '/'
    }

    if (import.meta.client) {
      window.location.href = `/api/auth/login?state=${encodeURIComponent(state)}`
      return suspendreJusquAuDepart()
    }

    return navigateTo(`/api/auth/login?state=${encodeURIComponent(state)}`, { external: true })
  }

  // Authentifié sur le SSO, mais sans rôle sur cette application : on arrête
  // ici plutôt que de laisser parcourir des pages dont chaque appel API
  // répondrait 403.
  const { canAccess } = useAuthorization()
  if (!canAccess.value) {
    throw createError({
      statusCode: 403,
      message: 'Aucun rôle ne vous a été attribué sur cette application. Contactez un administrateur.',
    })
  }
})
